import { useState } from 'react'
import {
  Link,
  createFileRoute,
  useNavigate,
  notFound,
  redirect,
} from '@tanstack/react-router'
import Pagination from '@/components/editorial/Pagination'
import { PAGE_SIZE, pageUrl, validatePageSearch } from '@/lib/pagination'
import { ArrowUpRight } from 'lucide-react'
import EditorialHero from '@/components/editorial/EditorialHero'
import EditorialPostList from '@/components/editorial/EditorialPostList'
import SectionHeader from '@/components/editorial/SectionHeader'
import Reveal from '@/components/preview/Reveal'
import { getBlogIndexData } from '@/lib/postServerFns'

function validateSearch(search: Record<string, unknown>): {
  tag?: string
  category?: string
  page?: number
} {
  return {
    ...validatePageSearch(search),
    tag: typeof search.tag === 'string' ? search.tag : undefined,
    category: typeof search.category === 'string' ? search.category : undefined,
  }
}

export const Route = createFileRoute('/blog/')({
  validateSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    if (deps.page === 0) throw notFound()
    if (deps.page === 1)
      throw redirect({
        to: '/blog',
        search: { tag: deps.tag, category: deps.category },
        statusCode: 301,
      })
    const data = await getBlogIndexData({ data: deps })
    if (!data.validPage) throw notFound()
    return {
      ...data,
      filtered: Boolean(deps.tag || deps.category),
      markdownSearch: deps,
    }
  },
  component: BlogIndex,
  head: ({ loaderData }) => {
    const page = loaderData?.page ?? 1
    const title = `Web Engineering Articles${page > 1 ? ` — Page ${page}` : ''} | Suriyaprakash Somu`
    const blogUrl = pageUrl('https://www.suriyaprakash.in/blog', page)
    const posts = loaderData?.posts ?? []
    const description =
      'Engineering articles by Suriyaprakash Somu on web fundamentals, backend systems, authentication and the implementation decisions behind full-stack applications.'

    return {
      meta: [
        {
          title,
        },
        {
          name: 'title',
          content: title,
        },
        {
          name: 'description',
          content: description,
        },
        {
          name: 'robots',
          content:
            !loaderData || loaderData.filtered
              ? 'noindex, follow'
              : 'index, follow',
        },
        { name: 'author', content: 'Suriyaprakash Somu' },
        { name: 'geo.region', content: 'IN-TN' },
        {
          name: 'geo.placename',
          content: 'Erode, Tamil Nadu, India',
        },
        {
          name: 'ai-summary',
          content:
            'The engineering blog of Suriyaprakash Somu, a full-stack developer in Erode, Tamil Nadu, India. It contains practical articles about React and Node.js development, access control, schema-driven forms, ERP systems, authentication, networking, security, and production application architecture.',
        },
        {
          name: 'keywords',
          content: [
            'blog',
            'full-stack development',
            'react',
            'node.js',
            'rbac',
            'erp',
            ...(loaderData?.allTags ?? []),
          ].join(', '),
        },
        { property: 'og:title', content: title },
        {
          property: 'og:description',
          content: description,
        },
        { property: 'og:type', content: 'website' },
        {
          property: 'og:url',
          content: blogUrl,
        },
        {
          property: 'og:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: description,
        },
      ],
      links: [
        { rel: 'canonical', href: blogUrl },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: 'https://www.suriyaprakash.in/rss.xml',
        },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Blog | Suriyaprakash Somu',
            description: description,
            url: blogUrl,
            isPartOf: {
              '@type': 'WebSite',
              name: 'Suriyaprakash Somu',
              url: 'https://www.suriyaprakash.in',
            },
          }),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Suriyaprakash Somu Blog Articles',
            itemListElement: posts.map((post, index) => ({
              '@type': 'ListItem',
              position: (page - 1) * PAGE_SIZE + index + 1,
              url: `https://www.suriyaprakash.in/blog/${post.category}/${post.slug}`,
              name: post.frontmatter.title,
            })),
          }),
        },
      ],
    }
  },
})

function BlogIndex() {
  const { posts, categories, allTags, totalPosts, page, totalPages } =
    Route.useLoaderData()
  const { tag: activeTag, category: activeCategory } = Route.useSearch()
  const navigate = useNavigate()
  const [showAllTags, setShowAllTags] = useState(false)

  return (
    <div className="min-h-screen bg-ink text-cream-50">
      <EditorialHero
        eyebrow={`Field notes / ${totalPosts} articles`}
        title="Thinking in"
        accent="public."
        description="Practical explanations of web fundamentals, backend systems and full-stack development, alongside implementation decisions and tradeoffs."
        meta={
          <div className="preview-mono grid grid-cols-2 gap-5 text-[9px] font-bold uppercase tracking-[0.18em] text-cream-50/65 sm:text-[10px] lg:grid-cols-1">
            <p>
              {categories.length}{' '}
              {categories.length === 1 ? 'category' : 'categories'}
            </p>
            <p>{allTags.length} topics indexed</p>
          </div>
        }
      />

      <section className="bg-ink py-20 sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="Article index"
            title="Notes from the"
            accent="work."
            description="Browse explanations, reusable patterns and engineering tradeoffs by category or topic."
            tone="dark"
          />

          <Reveal delay={100} className="mt-12 sm:mt-16">
            <div className="border-y border-white/15">
              <div className="grid border-b border-white/15 lg:grid-cols-[9rem_1fr]">
                <p className="preview-mono border-b border-white/15 py-4 text-[9px] font-bold uppercase tracking-[0.2em] text-sage-200 lg:border-b-0 lg:border-r lg:pr-5">
                  Discipline
                </p>
                <div className="flex flex-wrap lg:pl-5">
                  <button
                    type="button"
                    aria-pressed={!activeCategory && !activeTag}
                    onClick={() => navigate({ to: '/blog', search: {} })}
                    className={`preview-mono focus-ring border-r border-white/15 px-4 py-4 text-[9px] font-bold uppercase tracking-[0.16em] transition-colors ${
                      !activeCategory && !activeTag
                        ? 'bg-sage-200 text-forest-strong'
                        : 'text-cream-50/85 hover:bg-white/[0.05] hover:text-sage-200'
                    }`}
                  >
                    All / {totalPosts}
                  </button>
                  {categories.map((cat) => (
                    <button
                      type="button"
                      key={cat.slug}
                      aria-pressed={activeCategory === cat.slug}
                      onClick={() =>
                        navigate({
                          to: '/blog',
                          search: {
                            tag: activeTag,
                            category:
                              activeCategory === cat.slug
                                ? undefined
                                : cat.slug,
                          },
                        })
                      }
                      className={`preview-mono focus-ring border-r border-white/15 px-4 py-4 text-[9px] font-bold uppercase tracking-[0.16em] transition-colors ${
                        activeCategory === cat.slug
                          ? 'bg-sage-200 text-forest-strong'
                          : 'text-cream-50/85 hover:bg-white/[0.05] hover:text-sage-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {allTags.length > 0 && (
                <div className="grid lg:grid-cols-[9rem_1fr]">
                  <p className="preview-mono border-b border-white/15 py-4 text-[9px] font-bold uppercase tracking-[0.2em] text-sage-200 lg:border-b-0 lg:border-r lg:pr-5">
                    Topic
                  </p>
                  <div className="flex flex-wrap lg:pl-5">
                    {(showAllTags
                      ? allTags
                      : allTags.filter(
                          (tag, index) => index < 10 || tag === activeTag,
                        )
                    ).map((tag) => (
                      <button
                        type="button"
                        key={tag}
                        aria-pressed={activeTag === tag}
                        onClick={() =>
                          navigate({
                            to: '/blog',
                            search: {
                              category: activeCategory,
                              tag: activeTag === tag ? undefined : tag,
                            },
                          })
                        }
                        className={`preview-mono focus-ring border-r border-white/15 px-4 py-4 text-[9px] font-bold uppercase tracking-[0.14em] transition-colors ${
                          activeTag === tag
                            ? 'bg-sage-200 text-forest-strong'
                            : 'text-cream-50/65 hover:bg-white/[0.05] hover:text-sage-200'
                        }`}
                      >
                        #{tag}
                      </button>
                    ))}
                    {allTags.length > 10 && (
                      <button
                        type="button"
                        onClick={() => setShowAllTags((visible) => !visible)}
                        aria-expanded={showAllTags}
                        className="preview-mono focus-ring px-4 py-4 text-[9px] font-bold uppercase tracking-[0.14em] text-sage-200 transition-colors hover:bg-white/[0.05]"
                      >
                        {showAllTags
                          ? 'Show less'
                          : `+${allTags.length - 10} topics`}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Reveal>

          <div className="mt-10 sm:mt-14">
            {totalPosts === 0 ? (
              <Reveal>
                <div className="border-y border-white/15 py-16">
                  <p className="font-heading text-3xl text-cream-50 sm:text-5xl">
                    No articles published yet.
                  </p>
                  <p className="mt-4 text-sm text-cream-50/85">
                    Check back soon.
                  </p>
                </div>
              </Reveal>
            ) : posts.length === 0 ? (
              <Reveal>
                <div className="border-y border-white/15 py-16">
                  <p className="font-heading text-3xl text-cream-50 sm:text-5xl">
                    No articles match this index.
                  </p>
                  <Link
                    to="/blog"
                    className="preview-link-sweep preview-mono focus-ring mt-6 inline-flex text-[10px] font-bold uppercase tracking-[0.2em] text-sage-200"
                  >
                    Clear filters
                  </Link>
                </div>
              </Reveal>
            ) : (
              <EditorialPostList posts={posts} categories={categories} />
            )}
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            to="/blog"
            filters={{ tag: activeTag, category: activeCategory }}
          />
        </div>
      </section>

      <section className="bg-sage-100 py-20 text-ink sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="Keep exploring"
            title="Follow the"
            accent="threads."
            description="Browse the full taxonomy or open the interactive systems that sit behind these field notes."
            action={
              <Link
                to="/blog/tag"
                className="preview-link-sweep preview-mono focus-ring inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-forest"
              >
                All {allTags.length} tags
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            }
          />

          <div className="mt-12 border-b border-ink/15 sm:mt-16">
            {[
              {
                index: '01',
                label: 'Browse the taxonomy',
                detail: `${allTags.length} indexed topics`,
                to: '/blog/tag' as const,
              },
              {
                index: '02',
                label: 'DataTable system',
                detail: 'Production-style React demo',
                to: '/playground/datatable' as const,
              },
              {
                index: '03',
                label: 'SchemaForm builder',
                detail: 'Schema-driven React demo',
                to: '/playground/schemaform' as const,
              },
              {
                index: '04',
                label: 'QueryBuilder system',
                detail: 'Schema-driven filter demo',
                to: '/playground/querybuilder' as const,
              },
            ].map((item, index) => (
              <Reveal key={item.index} delay={index * 70}>
                <Link
                  to={item.to}
                  className="group grid grid-cols-[2.75rem_minmax(0,1fr)_auto] items-center gap-4 border-t border-ink/15 py-7 sm:grid-cols-[4rem_minmax(0,1fr)_auto] sm:gap-6 sm:py-9"
                >
                  <span className="preview-mono text-xs font-bold text-forest">
                    {item.index}
                  </span>
                  <span>
                    <span className="block font-heading text-3xl leading-none tracking-[-0.025em] transition-colors group-hover:text-forest sm:text-5xl">
                      {item.label}
                    </span>
                    <span className="preview-mono mt-2 block text-[9px] font-bold uppercase tracking-[0.16em] text-muted">
                      {item.detail}
                    </span>
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-forest transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
